/**
 * Shared React-Query hooks and query-key factory.
 *
 * Goals
 * -----
 * 1. **Single source of cached data** for the hottest reads: athletes, teams,
 *    quadrant data, athlete sprint/jump bundles. Hitting the same page twice
 *    now paints instantly from cache while a background revalidation runs.
 * 2. **Smooth athlete switching** on Sprint/Jump/Video pages via
 *    `placeholderData`: when the selected athlete changes, the previous
 *    athlete's data stays on screen until the new slice resolves — no blank
 *    flicker, no spinner wall.
 * 3. **Prefetch on intent** — `prefetchAthleteBundle(queryClient, aid)` lets
 *    callers warm the cache on hover / focus so the actual click feels free.
 * 4. **Guardrail — never change API shape.** Hooks return the exact payload
 *    the old `api.get(...)` returned so existing pages migrate 1-to-1.
 *
 * Query-key convention
 * --------------------
 * Keys live under `qk.*` so cache invalidations stay honest and typo-free.
 * Each key is an array; variable parts (ids, params) are appended as objects.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

// ─── Query key factory ──────────────────────────────────────────────────────
export const qk = {
  athletes:          () => ["athletes"],
  athlete:           (aid) => ["athlete", aid],
  athleteVideos:     (aid) => ["athlete", aid, "videos"],
  athleteSnapshots:  (aid) => ["athlete", aid, "snapshots"],
  athleteMechanics:  (aid) => ["athlete", aid, "mechanics"],
  athleteTests:      (aid) => ["athlete", aid, "tests"],
  teams:             () => ["teams"],
  team:              (tid) => ["team", tid],
  groups:            () => ["groups"],
  testDefinitions:   () => ["test-definitions"],
  issueLibrary:      (t) => ["issue-library", t],
  quadrant:          (params) => ["quadrant", params || {}],
  quadrantHistory:   (aid, fromDate) => ["quadrant", "history", aid, fromDate || ""],
  quadrantScoreGrid: () => ["quadrant", "score-grid"],
};

// ─── Fetchers ──────────────────────────────────────────────────────────────
// Each fetcher is a plain `async` function so it can be reused from the
// imperative prefetch helper below (queryClient.prefetchQuery needs a thunk).
const fetchers = {
  athletes:          () => api.get("/athletes").then((r) => r.data),
  athleteVideos:     (aid) => api.get(`/athletes/${aid}/videos`).then((r) => r.data),
  athleteSnapshots:  (aid) => api.get(`/athletes/${aid}/snapshots`).then((r) => r.data),
  athleteMechanics:  (aid) => api.get(`/athletes/${aid}/mechanics`).then((r) => r.data),
  athleteTests:      (aid) => api.get(`/athletes/${aid}/tests`).then((r) => r.data),
  teams:             () => api.get("/teams").then((r) => r.data),
  groups:            () => api.get("/groups").then((r) => r.data),
  testDefinitions:   () => api.get("/test-definitions").then((r) => r.data),
  issueLibrary:      (t) => api.get(`/issue-library?type=${t}`).then((r) => r.data),
  quadrant:          (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return api.get(`/quadrant${q ? `?${q}` : ""}`).then((r) => r.data);
  },
  quadrantHistory:   (aid, fromDate) => {
    const p = new URLSearchParams({ athlete_id: aid });
    if (fromDate) p.set("from_date", fromDate);
    return api.get(`/quadrant/history?${p.toString()}`).then((r) => r.data);
  },
  quadrantScoreGrid: () => api.get("/quadrant/score-grid").then((r) => r.data),
};

// `keepPrevious: (prev) => prev` → when the key changes (e.g. selectedId
// switches athlete), react-query keeps serving the previous data as
// `placeholderData` until the new fetch resolves. This is what makes
// "switching athletes feel instant".
const KEEP_PREV = { placeholderData: (prev) => prev };

// ─── Public hooks (named to mirror the API routes they wrap) ──────────────
export const useAthletes          = (opts = {}) => useQuery({ queryKey: qk.athletes(),         queryFn: fetchers.athletes,         ...opts });
export const useAthleteVideos     = (aid, opts = {}) => useQuery({ queryKey: qk.athleteVideos(aid),     queryFn: () => fetchers.athleteVideos(aid),     enabled: !!aid, ...KEEP_PREV, ...opts });
export const useAthleteSnapshots  = (aid, opts = {}) => useQuery({ queryKey: qk.athleteSnapshots(aid),  queryFn: () => fetchers.athleteSnapshots(aid),  enabled: !!aid, ...KEEP_PREV, ...opts });
export const useAthleteMechanics  = (aid, opts = {}) => useQuery({ queryKey: qk.athleteMechanics(aid),  queryFn: () => fetchers.athleteMechanics(aid),  enabled: !!aid, ...KEEP_PREV, ...opts });
export const useAthleteTests      = (aid, opts = {}) => useQuery({ queryKey: qk.athleteTests(aid),      queryFn: () => fetchers.athleteTests(aid),      enabled: !!aid, ...KEEP_PREV, ...opts });
export const useTeams             = (opts = {}) => useQuery({ queryKey: qk.teams(),            queryFn: fetchers.teams,            ...opts });
export const useGroups            = (opts = {}) => useQuery({ queryKey: qk.groups(),           queryFn: fetchers.groups,           ...opts });
export const useTestDefinitions   = (opts = {}) => useQuery({ queryKey: qk.testDefinitions(),  queryFn: fetchers.testDefinitions,  staleTime: 5 * 60_000, ...opts });
export const useIssueLibrary      = (t, opts = {}) => useQuery({ queryKey: qk.issueLibrary(t), queryFn: () => fetchers.issueLibrary(t), enabled: !!t, staleTime: 5 * 60_000, ...opts });
export const useQuadrant          = (params, opts = {}) => useQuery({ queryKey: qk.quadrant(params),      queryFn: () => fetchers.quadrant(params),       ...KEEP_PREV, ...opts });
export const useQuadrantHistory   = (aid, fromDate, opts = {}) => useQuery({ queryKey: qk.quadrantHistory(aid, fromDate), queryFn: () => fetchers.quadrantHistory(aid, fromDate), enabled: !!aid, ...KEEP_PREV, ...opts });
export const useQuadrantScoreGrid = (opts = {}) => useQuery({ queryKey: qk.quadrantScoreGrid(), queryFn: fetchers.quadrantScoreGrid, ...opts });

// ─── Imperative prefetch helpers (for hover / background warm-up) ─────────
/** Warm the cache for a single athlete's Sprint / Jump bundle. Fire-and-forget. */
export function prefetchAthleteBundle(queryClient, aid) {
  if (!aid) return;
  queryClient.prefetchQuery({ queryKey: qk.athleteVideos(aid),    queryFn: () => fetchers.athleteVideos(aid) });
  queryClient.prefetchQuery({ queryKey: qk.athleteSnapshots(aid), queryFn: () => fetchers.athleteSnapshots(aid) });
  queryClient.prefetchQuery({ queryKey: qk.athleteMechanics(aid), queryFn: () => fetchers.athleteMechanics(aid) });
  queryClient.prefetchQuery({ queryKey: qk.athleteTests(aid),     queryFn: () => fetchers.athleteTests(aid) });
}

/** Background warm-up right after login (called from AuthContext). */
export function warmGlobalCache(queryClient) {
  queryClient.prefetchQuery({ queryKey: qk.athletes(),        queryFn: fetchers.athletes });
  queryClient.prefetchQuery({ queryKey: qk.teams(),           queryFn: fetchers.teams }).catch(() => {});
  queryClient.prefetchQuery({ queryKey: qk.testDefinitions(), queryFn: fetchers.testDefinitions }).catch(() => {});
}

/** Convenience hook returning a helper bound to the current client. */
export function usePrefetchAthlete() {
  const qc = useQueryClient();
  return (aid) => prefetchAthleteBundle(qc, aid);
}

/** Invalidate everything related to one athlete (after an upload / edit). */
export function invalidateAthlete(queryClient, aid) {
  if (!aid) return;
  queryClient.invalidateQueries({ queryKey: ["athlete", aid] });
  // Quadrant snapshots embed per-athlete scores, so refresh those too.
  queryClient.invalidateQueries({ queryKey: ["quadrant"] });
}
